<template>
    <div>
        <div ref="mapContainer" class="mapContainer"></div>
    </div>
</template>

<script>
import {ref, onMounted, watch, nextTick} from 'vue';
import Map from 'ol/Map';
import View from 'ol/View';
import {Tile as TileLayer} from 'ol/layer';
import {XYZ} from 'ol/source';
import {Circle, Fill, Stroke, Style, Icon, Text} from 'ol/style';
import Point from 'ol/geom/Point';
import Feature from 'ol/Feature';
import {transform} from 'ol/proj';
import {boundingExtent} from 'ol/extent';
import {currencyFormatter, roundToThousand} from '@/pages/leadwidget/helpers';
import {createDefaultInteractions, updateInteractionsForTouch, createVectorLayer} from '@/utils/openLayersHelpers'

// TODO: get this dynamically from a config file?
const mapUrl = 'https://maps.immoparse.be/mapproxy/tiles/osm/osm_grid/{z}/{x}/{y}.png';
const mapProjection = 'EPSG:3857';
const sourceProjection = 'EPSG:4326';
const destinationProjection = mapProjection;

export default {
    name: 'OpenlayersMapElement',
    props: {
        lng: {
            type: Number,
        },
        lat: {
            type: Number,
        },
        strokeColor: {
            type: String,
            default: '#ffffff',
        },
        fillColor: {
            type: String,
            default: '#02a19a',
        },
        comparisonPoints: {
            type: Array,
            default: () => []
        },
        locale: {
            type: String,
            default: 'nl'
        },
        stylesheet: {
            type: String,
            default: 'ip'
        },
        showMarker: {
            type: Boolean,
            default: true
        },
        zoomLevel: {
            type: Number,
            default: 14
        },
        responsiveConfig: {
            type: Object,
            default: () => ({}),
        },
        extentPadding: {
            type: Array,
            default: () => [20, 35, 20, 400]
        }
    },
    setup(props) {
        const isTouchDevice = ref('ontouchstart' in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0);
        const mapContainer = ref(null);
        const map = ref(null);
        const radius = 8;
        const strokeWidth = 3.5;
        const center = [props.lng, props.lat];
        const extentPadding = ref(props.extentPadding); // Default padding extent
        const formattedComparisonPoints = props.comparisonPoints.map((comparisonPoint) => [comparisonPoint[0], transform([comparisonPoint[1], comparisonPoint[2]], sourceProjection, destinationProjection)]);

        const propertyFeatureIconUrl = ref('')
        const scale = ref(0)
        const svgContent = `
            <svg width="64px" height="64px" viewBox="-4 0 36 36" xmlns="http://www.w3.org/2000/svg">
                <path d="M14,0 C21.732,0 28,5.641 28,12.6 C28,23.963 14,36 14,36 C14,36 0,24.064 0,12.6 C0,5.641 6.268,0 14,0 Z" fill="${props.fillColor}"></path>
                <circle fill="${props.strokeColor}" cx="14" cy="14" r="7"></circle>
            </svg>
        `;

        if (props.fillColor && props.strokeColor) {
            propertyFeatureIconUrl.value = `data:image/svg+xml,${encodeURIComponent(svgContent)}`
            scale.value = 0.65
        } else {
            propertyFeatureIconUrl.value = '/static/icons/pinpoint_white_highres.png'
            scale.value = 0.35
        }

        // below handles responsiveness of map
        let screenWidth = window.innerWidth
        const updateScreenWidth = () => {
            screenWidth = window.innerWidth
              for (const screenSize in props.responsiveConfig) {
                if (screenWidth <= parseInt(screenSize)) {
                  extentPadding.value = props.responsiveConfig[screenSize].padding;
                  break;
                }
              }
        }

        const detectedTouchDevice = () => {
            isTouchDevice.value = true
        }

        const detectedDesktopDevice = () => {
            isTouchDevice.value = false
        }

        onMounted(async () => {
            await nextTick();
            updateScreenWidth() // necessary to determine padding to be applied to view
            window.addEventListener('resize', updateScreenWidth); // add event listener so extentPadding is updated when window resized
            window.addEventListener('touchmove', detectedTouchDevice); // add event listener to detect touch device
            ['pointerdown', 'mousemove'].forEach(eventType => window.addEventListener(eventType, detectedDesktopDevice)); // add event listener to detect desktop (using mouse) device

            const propertyFeature = new Feature(new Point(transform(center, sourceProjection, destinationProjection)));
            propertyFeature.setStyle(new Style({
                image: new Icon({
                    anchor: [0.5, 1],
                    scale: scale.value,
                    src: propertyFeatureIconUrl.value
                })
            }));

            const comparisonFeatures = formattedComparisonPoints.map(point => {
                const feature = new Feature(new Point(point[1]))
                feature.setStyle(new Style({
                    image: new Circle({
                        radius,
                        fill: new Fill({color: props.fillColor}),
                        stroke: new Stroke({color: props.strokeColor, width: strokeWidth})
                    }),
                    text: new Text({
                        font: '12px Helvetica, Verdana, Liberation Sans, sans-serif',
                        offsetY: 20,
                        stroke: new Stroke({color: '#fff', width: strokeWidth}),
                        text: (currencyFormatter({
                            locale: props.locale,
                            notation: 'compact',
                            compactDisplay: 'short',
                            style: 'null'
                        }).format(roundToThousand(point[0]))).toString(),
                    })
                }))
                return feature;
            });

            const propertyLayer = createVectorLayer({
                features: [propertyFeature],
                visible: props.showMarker,
                zIndex: 999
            });

            const comparisonLayer = createVectorLayer({
                features: comparisonFeatures,
                declutter: true,
            });

            const view = new View({
                center: transform([props.lng, props.lat], sourceProjection, destinationProjection),
                zoom: 14,
                rotation: 0,
                projection: mapProjection
            });

            let allowedInteractions = createDefaultInteractions(isTouchDevice.value)
            if (isTouchDevice.value) {
                updateInteractionsForTouch(allowedInteractions)
            }

            map.value = new Map({
                interactions: allowedInteractions,
                layers: [
                    new TileLayer({
                        source: new XYZ({
                            maxZoom: 22,
                            url: mapUrl
                        })
                    }),
                    propertyLayer,
                    comparisonLayer
                ],
                controls: [], // This removes all visible controls from map (zoom in/out,..)
                target: mapContainer.value,
                view
            });

            view.fit(boundingExtent([...formattedComparisonPoints.map((point) => point[1]), transform(center, sourceProjection, destinationProjection)]), {
                duration: 1000,
                padding: extentPadding.value,
                maxZoom: props.zoomLevel
            })


            watch(() => [props.lng, props.lat], ([newLng, newLat]) => {
                const newCenter = transform([newLng, newLat], sourceProjection, destinationProjection)
                propertyFeature.setGeometry(new Point(newCenter));
                view.setCenter(transform([newLng, newLat], sourceProjection, destinationProjection))
            });

            watch(() => props.comparisonPoints, (newComparisonPoints) => {
                const newFormattedComparisonPoints = newComparisonPoints.map((comparisonPoint) => [comparisonPoint[0], transform([comparisonPoint[1], comparisonPoint[2]], sourceProjection, destinationProjection)]);
                const newComparisonFeatures = newFormattedComparisonPoints.map(point => {
                    const feature = new Feature(new Point(point[1]));
                    feature.setStyle(new Style({
                        image: new Circle({
                            radius,
                            fill: new Fill({color: props.fillColor}),
                            stroke: new Stroke({color: props.strokeColor, width: strokeWidth}),
                            declutterMode: "none"
                        }),
                        text: new Text({
                            font: '12px Helvetica, Verdana, Liberation Sans, sans-serif',
                            offsetY: 20,
                            stroke: new Stroke({color: '#fff', width: strokeWidth}),
                            text: (currencyFormatter({
                                locale: props.locale,
                                notation: 'compact',
                                compactDisplay: 'short',
                                style: 'null'
                            }).format(roundToThousand(point[0]))).toString(),
                        })
                    }))
                    return feature
                });

                comparisonLayer.getSource().clear();
                comparisonLayer.getSource().addFeatures(newComparisonFeatures);

                view.fit(boundingExtent([...newFormattedComparisonPoints.map((point) => point[1]), transform(center, sourceProjection, destinationProjection)]), {
                    duration: 1000,
                    padding: extentPadding.value,
                    maxZoom: props.zoomLevel
                })
            })
        })

        return {
            mapContainer,
            isTouchDevice
        }
    },
};
</script>

<style scoped lang='scss'>
div.mapContainer {
  box-shadow: 0 2px 8px rgba(0, 0, 0, .15);
  height: 260px;
}

@media only screen and (max-width: 650px) {
  div.mapContainer {
    height: 450px;
  }
}
</style>